import { useCallback, useEffect, useMemo, useState } from 'react'
import { ViewProps } from '../../types'

export const skipFieldNames = [
  'noPublicConsent',
  'noVideoRecord',
  'givePublicReviewInstead',
  'noNegativeContact',
] as const

type SkipFieldName = typeof skipFieldNames[number]
export const isSkipFieldName = (name: string): name is SkipFieldName => skipFieldNames.includes(name as SkipFieldName)

/**
 * Hook to handle noPublicConsent or noVideoRecord skip checkbox
 * and then submit form
 */
export function useSkipButton(props: ViewProps) {
  const { state, dispatch, submitForm } = props
  const [shouldSubmit, setShouldSubmit] = useState(false)
  const [value, setValue] = useState(false)
  const skipFields = useMemo(() => {
    const fields = state.step.fields || []

    return fields.filter(f => isSkipFieldName(f.name))
  }, [state])

  const handleSkipClick = useCallback(
    (skipFieldName: string) => {
      const skipField = skipFields.find(sf => sf.name === skipFieldName)
      if (!skipField || !state) return
      dispatch({ type: 'set value', field: skipField, value: true })
      setValue(true)
      setShouldSubmit(true)
    },
    [skipFields, state, dispatch]
  )

  /**
   * We need to wait that state updates with correct value for skip field
   * and then we can submit form
   */
  useEffect(() => {
    if (!shouldSubmit) return
    if (!value) return
    submitForm()
  }, [submitForm, shouldSubmit, value])

  return {
    skipFields,
    handleSkipClick,
  }
}

export type SkipButtonState = ReturnType<typeof useSkipButton>
